<script>
    import { onMount, tick } from "svelte";

    import { preOpQuestionnaires } from "../../stores";

    import {
        Button,
        CardLayout,
        DecimalInput,
        Loader,
        NumericInput,
        PageHead,
        PercentageInput,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        checkAbnValidation,
        getEmailValidation,
        getLandlineValidation,
        getPhoneValidation,
        getTollFreeValidation,
        goToURL,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { cachePreOpQuestionnaire } from "../../Utils/utilCache.svelte";
    import {
        EmptyGroupDetails,
        MaxPaperProcessingFee,
    } from "../../Utils/utilConstants.svelte";
    import InfoIcon from "../../Components/Common/InfoIcon.svelte";

    let groupDetails = { ...EmptyGroupDetails };

    let isCreatingGroupDetails = false;

    $: isValidContactEmail = groupDetails.contactEmail
        ? getEmailValidation(groupDetails.contactEmail)
        : true;
    $: isValidContactPhone = groupDetails.contactPhone
        ? getPhoneValidation(groupDetails.contactPhone) ||
          getLandlineValidation(groupDetails.contactPhone) ||
          getTollFreeValidation(groupDetails.contactPhone)
        : true;
    $: isValidABN = groupDetails.abn
        ? checkAbnValidation(groupDetails.abn)
        : true;
    $: isValidBSB = groupDetails.bankAccountBSB
        ? BsbRegex.test(groupDetails.bankAccountBSB)
        : true;
    $: isValidAccountNumber = groupDetails.bankAccountNumber
        ? accountNumberRegex.test(groupDetails.bankAccountNumber)
        : true;
    $: isValidAdminEmail = groupDetails.adminEmail
        ? getEmailValidation(groupDetails.adminEmail)
        : true;
    $: isValidSupportEmail = groupDetails.supportEmail
        ? getEmailValidation(groupDetails.supportEmail)
        : true;
    $: isValidCustomerFacingEmail = groupDetails.customerFacingEmail
        ? getEmailValidation(groupDetails.customerFacingEmail)
        : true;

    $: isValidRefundsEmail = groupDetails.refundsEmail
        ? getEmailValidation(groupDetails.refundsEmail)
        : true;

    $: isSubmitBtnDisabled =
        isCreatingGroupDetails || _getSubmitBtnDisabled(groupDetails);

    let isLoadingPreOpQuestionnaires = false,
        filteredPreOpQuestionnaires = [];

    const BsbRegex = /^\d{6}$/;
    const accountNumberRegex = /^\d{6,10}$/;

    onMount(async () => {
        await loadPreOpQuestionnaires();
    });

    const loadPreOpQuestionnaires = async () => {
        isLoadingPreOpQuestionnaires = true;

        await cachePreOpQuestionnaire();

        // wait store reactive update finish
        await tick();

        filteredPreOpQuestionnaires = $preOpQuestionnaires.filter(
            (questionnaire) => !questionnaire.groupId,
        );

        isLoadingPreOpQuestionnaires = false;
    };

    const createGroup = async () => {
        isCreatingGroupDetails = true;

        _syncPercentageFee();

        await apiMethods.postMethod
            .createGroup(groupDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create group, please refresh the page and try again and contact support if the issue persists.",
                    );
                else goToURL(`/Groups/view/${res.id}`);
            })
            .catch((err) => showError(err));

        isCreatingGroupDetails = false;
    };

    // HELPER FUNCTIONS
    const _getSubmitBtnDisabled = (obj) =>
        !obj.name ||
        !isValidContactEmail ||
        !isValidContactPhone ||
        !isValidABN ||
        !isValidBSB ||
        !isValidAccountNumber ||
        !isValidAdminEmail ||
        !isValidSupportEmail ||
        !isValidRefundsEmail ||
        !isValidCustomerFacingEmail;

    const _syncPercentageFee = () => {
        if (!groupDetails.feePrivate) groupDetails.feePrivate = Number(0);
        else
            groupDetails.feePrivate = groupDetails.feePrivate
                .toString()
                .replace(" %", "");

        if (!groupDetails.feePublicInPrivate)
            groupDetails.feePublicInPrivate = Number(0);
        else
            groupDetails.feePublicInPrivate = groupDetails.feePublicInPrivate
                .toString()
                .replace(" %", "");
    };
</script>

<PageHead title="Create Group" />

<div class="flex flex-1 relative">
    <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4">
        <CardLayout title="Group Details">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2 items-center">
                        <p class="title">Name:</p>

                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.name}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Contact Name:</p>

                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.contactName}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">
                            <InfoIcon info={`This email will be used for internal communications with the group`}></InfoIcon>
                            Contact Email:
                        </p>

                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.contactEmail}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidContactEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">Contact Phone:</p>

                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.contactPhone}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidContactPhone}
                                <p class="text-error">Invalid Phone</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Address:</p>
                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.address}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Pre-Op Questionnaire:</p>

                        {#if isLoadingPreOpQuestionnaires}
                            <div class="flex gap-x-2">
                                <Loader />
                                <p>Loading Pre-Op Questionnaire</p>
                            </div>
                        {:else}
                            <select
                                class="content input input-sm input-bordered max-w-xs"
                                bind:value={groupDetails.preOpQuestionnaire}
                            >
                                <option
                                    value={Number(0)}
                                    selected={!groupDetails.preOpQuestionnaire}
                                >
                                    None
                                </option>
                                {#each filteredPreOpQuestionnaires as { id, name }}
                                    <option value={id}>
                                        {name}
                                    </option>
                                {/each}
                            </select>
                        {/if}
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            PreOp Questionnaire Notification Days:
                        </p>
                        <NumericInput
                            bind:value={groupDetails.preOpQuestionnaireNotifyDays}
                            inputClass="w-[calc(100%-150px)] max-w-xs"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex flex-col gap-y-4 items-start">
                        <p>PreOp Questionnaire Text in Communication:</p>
                        <input
                            type="text"
                            class="input input-sm input-bordered w-full"
                            autocomplete="off"
                            bind:value={groupDetails.preOpQuestionnaireTextInComm}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2">
                        <p class="title pt-1">ABN:</p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.abn}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidABN}
                                <p class="text-error">Invalid ABN</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Bank:</p>
                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.bank}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Bank Code:</p>
                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.bankCode}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Account Name:</p>
                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.bankAccountName}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">Bank Account BSB:</p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.bankAccountBSB}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidBSB}
                                <p class="text-error">Invalid BSB</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">Bank Account Number:</p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.bankAccountNumber}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidAccountNumber}
                                <p class="text-error">Invalid Number</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Minor ID:</p>
                        <input
                            type="text"
                            class="content input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.minorId}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2 items-center">
                        <p class="title">Fee Private (%):</p>
                        <PercentageInput
                            inputClass="w-[calc(100%-150px)] max-w-xs"
                            bind:value={groupDetails.feePrivate}
                            isDisabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Fee Public in Private (%):</p>
                        <PercentageInput
                            inputClass="w-[calc(100%-150px)] max-w-xs"
                            bind:value={groupDetails.feePublicInPrivate}
                            isDisabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Fixed Fee per Doctor:</p>
                        <DecimalInput
                            inputClass="w-[calc(100%-150px)] max-w-xs"
                            bind:value={groupDetails.fixedFeePerDoctor}
                            isDisabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">
                            <InfoIcon info={`If Send Doctors Emails to Admin is turned on, some doctor emails will be sent to this address instead. This also receives some notifications including patient declined consent, partial payment, and claim declined`}></InfoIcon>
                            Admin Email:
                        </p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.adminEmail}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidAdminEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`All doctor correspondence will be directed to the admin email`}></InfoIcon>
                            Send Doctors Emails to Admin:
                        </p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.sendDoctorsEmailsToAdmin}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            Send Fortnightly Invoices:
                        </p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.sendFortnightlyInvoice}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`This applies to annual, quarterly and fortnightly account invoices`}></InfoIcon>
                            Send Doctors Invoices Directly to Doctors:
                        </p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.sendDoctorsInvoicesToDoctors}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">
                            <InfoIcon info={`Receives notifications including payment due, and patient update details`}></InfoIcon>
                            Support Email:
                        </p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.supportEmail}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidSupportEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">
                            <InfoIcon info={`This is used in all email communications to patients in the email signature`}></InfoIcon>
                            Customer Facing Email:
                        </p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.customerFacingEmail}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidCustomerFacingEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">
                            <InfoIcon info={`This is used for emails relating to refunds`}></InfoIcon>
                            Refunds Email:
                        </p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                bind:value={groupDetails.refundsEmail}
                                disabled={isCreatingGroupDetails}
                            />

                            {#if !isValidRefundsEmail}
                                <p class="text-error">Invalid Email</p>
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-2">
                        <p class="title pt-1">SMS Originator:</p>
                        <div class="content flex flex-col gap-y-2">
                            <input
                                type="text"
                                class="w-full input input-sm input-bordered max-w-xs"
                                autocomplete="off"
                                maxlength="11"
                                bind:value={groupDetails.smsOriginator}
                                disabled={isCreatingGroupDetails}
                            />
                        </div>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Avoid Using Plus Sign in Emails:</p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.avoidPlusInEmails}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Add Phone to Notifications:</p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.addPhoneToComms}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>
                </div>
            </div>

            <div class="divider" />

            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2 items-center">
                        <p class="title">Hand Over:</p>

                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.handOver}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Auto Reconcile ERAs:</p>

                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.autoReconcileERAs}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Patient Pay Directly to Doctors:</p>

                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.patientPaymentsDirectlyToDoctors}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            Insurance Funds Pay Directly to Doctors:
                        </p>

                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={groupDetails.insurancePaymentsDirectlyToDoctors}
                            disabled={isCreatingGroupDetails}
                        />
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`If a due date is not set in an encounter, and the quote is for prepayment, the due date will be set ${groupDetails.estimatesDefaultDueDays} business days before the scheduled procedure date`}></InfoIcon>
                            Quotes Default Due Days:
                        </p>
                        <NumericInput
                            bind:value={groupDetails.estimatesDefaultDueDays}
                            inputClass="flex-1"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`After sending out a fee estimate, the receiver will have ${groupDetails.quotesOnlyDefaultDueDays} calendar days to accept the estimate`}></InfoIcon>
                            Fee Estimates Default Due Days:
                        </p>
                        <NumericInput
                            bind:value={groupDetails.quotesOnlyDefaultDueDays}
                            inputClass="flex-1"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`After changing an encounter’s status to Submitted, the system will automatically increment the status to In Progress, claim if appropriate, and request payment from the patient, if appropriate, after ${groupDetails.claimDelayHours} hours`}></InfoIcon>
                            Default Claim Delay (Hours):
                        </p>
                        <NumericInput
                            bind:value={groupDetails.claimDelayHours}
                            min="1"
                            max="72"
                            showInvalid
                            inputClass="flex-1"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>
                </div>

                <div class="divider lg:divider-horizontal" />

                <div class="flex flex-col flex-1 gap-y-4 h-full">
                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`When creating an invoice, the due date for any patient payments will be ${groupDetails.invoicesDefaultDueDays} calendar days in the future`}></InfoIcon>
                            Invoices Default Due Days:
                        </p>
                        <NumericInput
                            bind:value={groupDetails.invoicesDefaultDueDays}
                            inputClass="flex-1"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">
                            <InfoIcon info={`When creating an invoice, the due date for any insurer payments will be ${groupDetails.insuranceInvoicesDefaultDueDays} calendar days in the future`}></InfoIcon>
                            Insurance Invoices Default Due Days:
                        </p>
                        <NumericInput
                            bind:value={groupDetails.insuranceInvoicesDefaultDueDays}
                            inputClass="flex-1"
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <p class="title">Paper Processing Fee:</p>
                        <DecimalInput
                            bind:value={groupDetails.paperProcessingFee}
                            inputClass={groupDetails.paperProcessingFee >
                            MaxPaperProcessingFee
                                ? "w-[calc(100%-150px)] max-w-xs input-error"
                                : "w-[calc(100%-150px)] max-w-xs"}
                            isDisable={isCreatingGroupDetails}
                        />
                    </div>

                    {#if groupDetails.paperProcessingFee > MaxPaperProcessingFee}
                        <div class="flex gap-x-2 items-center">
                            <p class="text-error">
                                The maximum allowed paper processing fee is {MaxPaperProcessingFee}
                            </p>
                        </div>
                    {/if}
                </div>
            </div>
        </CardLayout>
    </div>
</div>

<div class="flex items-center justify-end pt-2">
    <Button
        clickFunc={createGroup}
        isDisabled={isSubmitBtnDisabled}
        manualClass={`${isCreatingGroupDetails ? "loading" : ""}`}
    >
        {isCreatingGroupDetails ? "Creating" : "Create"}
    </Button>
</div>

<style>
    .title {
        width: 150px;
    }

    .content {
        width: calc(100% - 150px);
    }
</style>
